// Drives the collapsible "Show content" accordions (rendered from BBCode
// [QUOTE]/[SPOILER] and the TinyMCE accordion plugin as
// <details class="mce-accordion">).
//
// Native <details> is unreliable for a "peek + fade + read more" layout:
// when closed, browsers hide the body via an internal ::details-content slot
// that CSS `display: block` on the body cannot override. So instead we keep
// every <details> permanently `open` (browser always lays out the body) and
// drive the collapsed/expanded visual entirely through a `pe-acc-collapsed`
// class. All peek/fade/clamp styling in app.css keys off that class.
//
// New accordions (after a Livewire morph/navigation) are picked up via
// MutationObserver, mirroring pe-carousel.js.

const INIT_MARK = '__peAccordionInit'
const DURATION_MS = 200
// Show a second collapse bar at the top once an open accordion is taller than
// this, so a long spoiler can be closed without scrolling down to the summary.
const TALL_THRESHOLD = 400

const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

function expand(details, body) {
    if (prefersReducedMotion()) {
        details.classList.remove('pe-acc-collapsed')
        return
    }
    const start = body.clientHeight
    details.classList.remove('pe-acc-collapsed') // drops the CSS max-height clamp
    const end = body.scrollHeight
    body.style.overflow = 'hidden'
    body.style.maxHeight = start + 'px'
    void body.offsetHeight // force reflow so the start value is committed
    body.style.transition = `max-height ${DURATION_MS}ms ease`
    body.style.maxHeight = end + 'px'

    const done = (e) => {
        // Ignore max-height transitions bubbling up from a nested accordion body
        // — only this body's own transition should finalise it.
        if (e.target !== body || e.propertyName !== 'max-height') {
            return
        }
        body.style.transition = ''
        body.style.maxHeight = ''
        body.style.overflow = ''
        body.removeEventListener('transitionend', done)
    }
    body.addEventListener('transitionend', done)
}

function collapse(details, body) {
    if (prefersReducedMotion()) {
        details.classList.add('pe-acc-collapsed')
        return
    }
    const start = body.scrollHeight
    body.style.overflow = 'hidden'
    body.style.maxHeight = start + 'px'
    void body.offsetHeight // force reflow
    body.style.transition = `max-height ${DURATION_MS}ms ease`
    details.classList.add('pe-acc-collapsed') // brings back the gradient + floating summary
    body.style.maxHeight = '160px'

    const done = (e) => {
        // Ignore max-height transitions bubbling up from a nested accordion body.
        if (e.target !== body || e.propertyName !== 'max-height') {
            return
        }
        // Hand height control back to the CSS class (max-height: 160px).
        body.style.transition = ''
        body.style.maxHeight = ''
        body.style.overflow = ''
        body.removeEventListener('transitionend', done)
    }
    body.addEventListener('transitionend', done)
}

// Show/hide the top collapse bar: only while this accordion is open AND its
// body is taller than the threshold. scrollHeight is the full content height
// regardless of the collapsed max-height clamp, so the check is stable.
function updateTopClose(details, body) {
    const topClose = details.querySelector(':scope > .pe-acc-top-close')
    if (!topClose) {
        return
    }
    const expanded = !details.classList.contains('pe-acc-collapsed')
    const tall = body.scrollHeight > TALL_THRESHOLD
    topClose.classList.toggle('pe-acc-top-close-visible', expanded && tall)
}

function initAccordion(details) {
    if (details[INIT_MARK]) {
        return
    }
    details[INIT_MARK] = true

    // Keep it technically open so the browser never hides the body content.
    details.open = true
    // Start collapsed (peek view).
    details.classList.add('pe-acc-collapsed')

    const summary = details.querySelector(':scope > .mce-accordion-summary')
    const body = details.querySelector(':scope > .mce-accordion-body')
    if (!summary || !body) {
        return
    }

    // Second collapse bar at the top (mirrors the summary's title). Hidden by
    // default; updateTopClose reveals it for tall, open accordions.
    const topClose = document.createElement('div')
    topClose.className = 'pe-acc-top-close'
    topClose.textContent = summary.textContent
    topClose.setAttribute('aria-hidden', 'true')
    details.insertBefore(topClose, details.firstChild)
    topClose.addEventListener('click', (event) => {
        event.preventDefault()
        collapse(details, body)
        updateTopClose(details, body)
    })

    summary.addEventListener('click', (event) => {
        // Stop native <details> from toggling the `open` attribute — we manage
        // visual state ourselves and want it to stay open at the DOM level.
        event.preventDefault()
        if (details.classList.contains('pe-acc-collapsed')) {
            expand(details, body)
        } else {
            collapse(details, body)
        }
        updateTopClose(details, body)
    })
}

function initAll(root) {
    const scope = root || document
    if (scope.querySelectorAll) {
        scope.querySelectorAll('details.mce-accordion').forEach(initAccordion)
    }
}

function start() {
    initAll(document)

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) {
                    continue
                }
                if (node.matches && node.matches('details.mce-accordion')) {
                    initAccordion(node)
                }
                if (node.querySelectorAll) {
                    node.querySelectorAll('details.mce-accordion').forEach(initAccordion)
                }
            }
        }
    })
    observer.observe(document.body, { childList: true, subtree: true })
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
} else {
    start()
}
